import type {
    GenerationBlockAvgScoreSchema,
    GenerationCriteriaAvgScoreSchema,
    ProductCardLlmBlockTypes,
} from '@/common/api/client/types.gen'
import { DASHBOARD_BLOCK_TYPES } from './scoreColors'

export const BRAND_COMPARE_COLORS = {
    brandA: '#08AC81',
    brandB: '#8b5cf6',
} as const

export type BrandCompareSide = 'brandA' | 'brandB'

export type DeltaResult = {
    value: number
    sign: 'positive' | 'negative' | 'equal'
    /** Positive delta from brandA perspective (a - b). */
    raw: number
}

export type LeaderResult = { type: 'equal' } | { type: 'leader'; side: BrandCompareSide; name: string }

export const computeDelta = (a: number, b: number): DeltaResult => {
    const raw = a - b
    if (Math.abs(raw) < 0.05) return { value: 0, sign: 'equal', raw: 0 }
    return {
        value: Math.abs(raw),
        sign: raw > 0 ? 'positive' : 'negative',
        raw,
    }
}

export const formatDeltaBadge = (
    delta: DeltaResult,
    options?: { fractionDigits?: number; suffix?: string }
): string => {
    const { fractionDigits = 1, suffix = '' } = options ?? {}
    if (delta.sign === 'equal') return '≈ равно'
    const formatted = fractionDigits === 0 ? String(Math.round(delta.value)) : delta.value.toFixed(fractionDigits)
    const prefix = delta.raw > 0 ? '+' : '-'
    return `${prefix}${formatted}${suffix}`
}

export const formatBlockDeltaBadge = (delta: DeltaResult, brandAName: string, brandBName: string): string => {
    if (delta.sign === 'equal') return '≈ равно'
    const leaderName = delta.raw > 0 ? brandAName : brandBName
    return `Δ ${Math.round(delta.value)}% · ↑ ${leaderName}`
}

export const getLeader = (a: number, b: number, brandAName: string, brandBName: string): LeaderResult => {
    const delta = computeDelta(a, b)
    if (delta.sign === 'equal') return { type: 'equal' }
    return {
        type: 'leader',
        side: delta.raw > 0 ? 'brandA' : 'brandB',
        name: delta.raw > 0 ? brandAName : brandBName,
    }
}

export type MergedScoreDistributionDatum = {
    score: number
    label: string
    brandACount: number
    brandBCount: number
}

export const mergeScoreDistributions = (
    scoresA: Array<{ score: number; count: number }> | undefined,
    scoresB: Array<{ score: number; count: number }> | undefined
): MergedScoreDistributionDatum[] => {
    const mapA = new Map((scoresA ?? []).map(item => [item.score, item.count]))
    const mapB = new Map((scoresB ?? []).map(item => [item.score, item.count]))

    return Array.from({ length: 10 }, (_, index) => {
        const score = index + 1
        return {
            score,
            label: String(score),
            brandACount: mapA.get(score) ?? 0,
            brandBCount: mapB.get(score) ?? 0,
        }
    })
}

export type MergedCriteriaRow = {
    criteriaName: string
    label: string
    brandAPercent: number
    brandBPercent: number
    delta: DeltaResult
    leader: LeaderResult
}

export type MergedBlockCriteria = {
    blockType: ProductCardLlmBlockTypes
    title?: string | null
    criterias: MergedCriteriaRow[]
}

const findBlock = (blocks: GenerationBlockAvgScoreSchema[] | undefined, blockType: ProductCardLlmBlockTypes) =>
    blocks?.find(block => block.block_type === blockType)

const criteriaKey = (criteria: GenerationCriteriaAvgScoreSchema) => criteria.criteria_name

export const mergeBlockCriteria = (
    blocksA: GenerationBlockAvgScoreSchema[] | undefined,
    blocksB: GenerationBlockAvgScoreSchema[] | undefined,
    brandAName: string,
    brandBName: string,
    getCriteriaLabel: (criteria: GenerationCriteriaAvgScoreSchema) => string
): MergedBlockCriteria[] =>
    DASHBOARD_BLOCK_TYPES.map(blockType => {
        const blockA = findBlock(blocksA, blockType)
        const blockB = findBlock(blocksB, blockType)
        const criteriaMap = new Map<string, GenerationCriteriaAvgScoreSchema>()

        for (const criteria of blockA?.criterias ?? []) {
            criteriaMap.set(criteriaKey(criteria), criteria)
        }
        for (const criteria of blockB?.criterias ?? []) {
            if (!criteriaMap.has(criteriaKey(criteria))) {
                criteriaMap.set(criteriaKey(criteria), criteria)
            }
        }

        const criterias: MergedCriteriaRow[] = Array.from(criteriaMap.entries()).map(([criteriaName, sample]) => {
            const percentA = blockA?.criterias?.find(c => c.criteria_name === criteriaName)?.percent ?? 0
            const percentB = blockB?.criterias?.find(c => c.criteria_name === criteriaName)?.percent ?? 0
            const delta = computeDelta(percentA, percentB)

            return {
                criteriaName,
                label: getCriteriaLabel(sample),
                brandAPercent: percentA,
                brandBPercent: percentB,
                delta,
                leader: getLeader(percentA, percentB, brandAName, brandBName),
            }
        })

        return {
            blockType,
            title: blockA?.title ?? blockB?.title,
            criterias,
        }
    }).filter(block => block.criterias.length > 0)
